/**
 * IC "trail after the 50% target" (server PR #130) — rebuild the trail's
 * arm point, confirmed-peak and floor history for the P&L chart.
 *
 * Server rule (app/services/iron_condor_strategy.py, TRAIL AFTER THE
 * TARGET; docs/ic-risk-calibration.md "Trail after the 50% target"):
 *   - only on a cycle whose conditional hold did NOT arm (holdArmed);
 *   - the observation that reaches the 50% target arms the trail
 *     (TRAIL_ARMED {pnlAtArm, targetPnl, trailPct, floorPnl}) and the
 *     peak starts at that P&L;
 *   - a new peak counts only when two CONSECUTIVE observations both
 *     clear it (peak = the lower of the two);
 *   - floor = peak x (1 - trailPct/100); P&L at/below it = TRAIL_FLOOR exit.
 *
 * Every check_and_manage call (minute tick, fast recheck, stream-routed)
 * broadcasts exactly one position_mark BEFORE its trail check, so the mark
 * series is the server's observation series. Marks carry trailPeakPnl /
 * trailFloorPnl (server exposure PR) = the state in effect as that tick
 * began; this module applies the one deterministic update the tick then
 * made, so the drawn step lands on the tick that confirmed it. Marks from
 * before that exposure (e.g. 2026-11-03) carry only trailArmed: the peak
 * is replayed from the arm with the same rule, seeded by the day's
 * TRAIL_ARMED event when the /marks response has it.
 *
 * Display-only. Never feeds a decision.
 */

export interface TrailMarkLike {
  at: string;
  unrealizedPnl: number;
  targetPnl?: number;
  holdArmed?: boolean;
  trailArmed?: boolean;
  trailPct?: number | null;
  trailPeakPnl?: number | null;
  trailFloorPnl?: number | null;
}

/** /api/bot/{id}/marks -> dayTrail[] (TRAIL_ARMED / TRAIL_FLOOR events). */
export interface TrailEvent {
  type: 'TRAIL_ARMED' | 'TRAIL_FLOOR' | string;
  at: string;
  pnlAtArm?: number;
  targetPnl?: number;
  trailPct?: number;
  floorPnl?: number;
}

export interface TrailStep {
  at: string;
  peak: number;
  /** null when the trail % is unknown (no event, pre-exposure marks). */
  floor: number | null;
}

export interface TrailModel {
  armAt: string;
  pnlAtArm: number;
  targetPnl: number | null;
  trailPct: number | null;
  /** Step series, first entry = the arm. Each entry holds until the next. */
  steps: TrailStep[];
  /** Time of the last observation while trailing (the series end). */
  endAt: string;
  /** TRAIL_FLOOR close, when the day's events have one. */
  exitAt: string | null;
}

const floorOf = (peak: number, pct: number | null) =>
  pct == null ? null : Math.round(peak * (1 - pct / 100) * 100) / 100;

/**
 * Build the day's trail from marks (+ optional day events). Returns null
 * when the trail never armed — including every held cycle, which never
 * trails by rule. Only the first trail of the day is modelled (one IC
 * cycle per day in practice; a later cycle's marks end the segment).
 */
export function buildTrail(
  points: TrailMarkLike[],
  events: TrailEvent[] | null | undefined,
): TrailModel | null {
  if (!points.length) return null;
  const armEv = (events || []).find((e) => e.type === 'TRAIL_ARMED') || null;

  // Index of the first mark AFTER the arming observation.
  let first = -1;
  let armAt: string;
  let pnlAtArm: number;
  let targetPnl: number | null;
  let trailPct: number | null;
  if (armEv) {
    first = points.findIndex((p) => p.at > armEv.at);
    armAt = armEv.at;
    pnlAtArm = Number(armEv.pnlAtArm);
    targetPnl = armEv.targetPnl ?? null;
    trailPct = armEv.trailPct ?? null;
    if (!Number.isFinite(pnlAtArm)) return null;
  } else {
    // Live/replay without the event: the arming tick's mark is the one
    // right before the first trailArmed=true mark (it was broadcast just
    // before the arm), and its P&L is the arm P&L.
    first = points.findIndex((p, i) => i > 0 && p.trailArmed === true
      && points[i - 1].trailArmed !== true);
    if (first <= 0) {
      if (points[0]?.trailArmed !== true) return null;
      // Series starts already armed (page opened mid-trail with a
      // truncated ring): anchor on the first mark's server peak.
      first = 0;
    }
    const armMark = first > 0 ? points[first - 1] : points[0];
    armAt = armMark.at;
    pnlAtArm = first > 0
      ? (points[first].trailPeakPnl ?? armMark.unrealizedPnl)
      : (points[0].trailPeakPnl ?? points[0].unrealizedPnl);
    targetPnl = armMark.targetPnl ?? null;
    trailPct = points[first].trailPct ?? null;
  }
  // Held cycles never trail — guard against a mislabelled event too.
  const segStart = first < 0 ? points.length : first;
  if (points.slice(0, segStart).some((p) => p.holdArmed)) return null;

  let peak = pnlAtArm;
  let prev = pnlAtArm;
  const steps: TrailStep[] = [{
    at: armAt, peak,
    floor: armEv?.floorPnl ?? floorOf(peak, trailPct),
  }];
  let endAt = armAt;
  for (let i = segStart; i < points.length; i++) {
    const m = points[i];
    // A mark that says "not trailing" after the arm = the cycle ended
    // and a new one began (or the server restarted the cycle state).
    if (m.trailArmed === false) break;
    if (m.trailPct != null && trailPct == null) trailPct = m.trailPct;
    if (m.trailPeakPnl != null) peak = m.trailPeakPnl;   // server truth
    const cur = m.unrealizedPnl;
    if (Math.min(prev, cur) > peak) peak = Math.min(prev, cur);
    prev = cur;
    const floor = floorOf(peak, trailPct);
    const last = steps[steps.length - 1];
    if (peak !== last.peak || floor !== last.floor) {
      steps.push({ at: m.at, peak, floor });
    }
    endAt = m.at;
  }
  const exitEv = (events || []).find((e) => e.type === 'TRAIL_FLOOR' && e.at >= armAt);
  return {
    armAt, pnlAtArm, targetPnl, trailPct, steps, endAt,
    exitAt: exitEv ? exitEv.at : null,
  };
}

/** Hours-since-midnight ET from the server's ET ISO stamp. */
export const toHours = (at: string) => {
  const hh = Number(at.slice(11, 13));
  const mm = Number(at.slice(14, 16));
  const ss = Number(at.slice(17, 19)) || 0;
  return hh + mm / 60 + ss / 3600;
};

/**
 * The trail as it stood at the playhead: REBUILT from only the marks and
 * events at/before `cutoffH` (ET hours), never clipped from the full-day
 * model, so a replayed day shows exactly what a live day showed at that
 * moment — not armed before the arm, the floor as of then, and the exit
 * only once the playhead passes it. `cutoffH == null` = the whole day.
 */
export function buildTrailUpTo(
  points: TrailMarkLike[],
  events: TrailEvent[] | null | undefined,
  cutoffH: number | null,
): TrailModel | null {
  if (cutoffH == null) return buildTrail(points, events);
  return buildTrail(
    points.filter((p) => toHours(p.at) <= cutoffH),
    (events || []).filter((e) => toHours(e.at) <= cutoffH),
  );
}

/** The step in effect at time `at` (ISO, same ET stamping), or null. */
export function trailAt(model: TrailModel, at: string): TrailStep | null {
  if (at < model.armAt || at > model.endAt) return null;
  let cur: TrailStep | null = null;
  for (const s of model.steps) {
    if (s.at <= at) cur = s;
    else break;
  }
  return cur;
}
